import { useEffect, useState } from "react";
import { Loader2, Pencil, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useHoroscopeSigns, useUpdateHoroscopeSign } from "@/hooks/useHoroscopeSigns";
import { useToast } from "@/hooks/use-toast";
import type { HoroscopeSign } from "@/lib/api/horoscope.service";

export type { HoroscopeSign };

const HOROSCOPE_LANGUAGE = "en";

export function ZodiacSignsTab() {
  const [editingSign, setEditingSign] = useState<HoroscopeSign | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    return () => {
      if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
    };
  }, [previewObjectUrl]);

  const { data: signs = [], isLoading, isError } = useHoroscopeSigns({
    language: HOROSCOPE_LANGUAGE,
  });

  const updateMutation = useUpdateHoroscopeSign(HOROSCOPE_LANGUAGE);

  const resetDialogState = () => {
    if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
    setPreviewObjectUrl(null);
    setEditingSign(null);
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const openEditDialog = (sign: HoroscopeSign) => {
    if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
    setPreviewObjectUrl(null);
    setPreviewUrl(sign.backgroundImageUrl || "");
    setSelectedFile(null);
    setEditingSign(sign);
  };

  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      e.target.value = "";
      toast({
        title: "Not an image",
        description: "Choose an image file (JPG, PNG, WebP…).",
        variant: "destructive",
      });
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      e.target.value = "";
      toast({
        title: "Image too large",
        description: "Background images must be 5 MB or smaller.",
        variant: "destructive",
      });
      return;
    }
    if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
    const url = URL.createObjectURL(file);
    setPreviewObjectUrl(url);
    setSelectedFile(file);
    setPreviewUrl(url);
  };

  const handleSave = () => {
    if (!editingSign || !selectedFile) return;
    updateMutation.mutate(
      {
        id: editingSign.id,
        file: selectedFile,
      },
      {
        onSuccess: () => {
          resetDialogState();
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        data-testid="zodiac-signs-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-center"
        data-testid="zodiac-signs-error"
      >
        <div className="p-4 rounded-full bg-muted mb-4">
          <Star className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-1">
          Unable to load zodiac signs
        </h3>
        <p className="text-sm text-muted-foreground">
          Failed to load zodiac signs. Please try again later.
        </p>
      </div>
    );
  }

  if (signs.length === 0) {
    return (
      <div
        className="flex flex-col items-center justify-center py-16 text-center"
        data-testid="zodiac-signs-empty"
      >
        <div className="p-4 rounded-full bg-muted mb-4">
          <Star className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-1">
          No zodiac signs configured
        </h3>
        <p className="text-sm text-muted-foreground">
          Zodiac sign data will appear here once configured
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {signs.map((sign) => {
          return (
            <Card
              key={sign.id}
              className="hover-elevate overflow-visible"
              data-testid={`card-sign-${sign.slug}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span
                      className="text-3xl leading-none"
                      data-testid={`text-symbol-${sign.slug}`}
                    >
                      {sign.symbol}
                    </span>
                    <h3
                      className="font-semibold text-foreground"
                      data-testid={`text-name-${sign.slug}`}
                    >
                      {sign.name}
                    </h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(sign)}
                    data-testid={`button-edit-sign-${sign.slug}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog
        open={!!editingSign}
        onOpenChange={(open) => {
          if (!open) {
            resetDialogState();
          }
        }}
      >
        <DialogContent data-testid="dialog-edit-sign">
          <DialogHeader>
            <DialogTitle>Edit {editingSign?.name} Background</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-backgroundImageUrl">
                Background Image
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="edit-backgroundImageUrl"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  data-testid="input-edit-backgroundImageUrl"
                />
              </div>
              {previewUrl && (
                <div className="mt-2">
                  <img
                    src={previewUrl}
                    alt="Background preview"
                    className="w-full h-32 object-cover rounded-md border"
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={resetDialogState}
              data-testid="button-cancel-edit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={updateMutation.isPending || !selectedFile}
              data-testid="button-save-sign"
            >
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
